import { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Add as AddIcon, Folder as FolderIcon } from '@mui/icons-material';
import { useContextStore } from '../../store/contextStore';

/**
 * Project switcher component for selecting the current project.
 */
export const ProjectSwitcher = () => {
  const { currentTenant, currentProject, availableProjects, setProject, loadProjects, isLoading } = useContextStore();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Load projects when tenant changes
    if (currentTenant && availableProjects.length === 0) {
      loadProjects(currentTenant.id);
    }
  }, [currentTenant, availableProjects.length, loadProjects]);

  // Keyboard shortcut: Ctrl+P
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        if (currentTenant) {
          setOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTenant]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const projectId = event.target.value;
    if (projectId === 'create') {
      // TODO: Open create project dialog
      return;
    }

    const selectedProject = availableProjects.find((p) => p.id === projectId);
    if (selectedProject) {
      setProject(selectedProject);
    }
  };

  const displayValue = currentProject?.name || 'Select Project';

  if (!currentTenant) {
    return (
      <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FolderIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          Select tenant first
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
      <FolderIcon sx={{ color: 'text.secondary' }} />
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="project-select-label">Project</InputLabel>
        <Select
          labelId="project-select-label"
          id="project-select"
          value={currentProject?.id || ''}
          label="Project"
          onChange={handleChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disabled={isLoading || availableProjects.length === 0}
          renderValue={(value) => {
            const project = availableProjects.find((p) => p.id === value);
            if (!project) return displayValue;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{project.name}</Typography>
                {!project.isActive && (
                  <Chip label="Inactive" size="small" color="error" sx={{ height: 20 }} />
                )}
              </Box>
            );
          }}
        >
          {availableProjects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography variant="body2">{project.name}</Typography>
                {!project.isActive && (
                  <Chip label="Inactive" size="small" color="error" sx={{ height: 20, ml: 1 }} />
                )}
              </Box>
            </MenuItem>
          ))}
          <MenuItem value="create" disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon fontSize="small" />
              <Typography variant="body2">Create New Project</Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};
